/**
 * /login — email + password, with MFA-required follow-on.
 *
 * On a 200 with status="ok" we refresh the auth context and
 * redirect to the workspace; on status="mfa_required" we navigate
 * to /mfa/challenge with the email and user_id pinned in router
 * state.
 */

import { type FormEvent, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { authApi, type AuthApiError } from "../../auth/api";

interface LocationState {
  from?: string;
}

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await authApi.login(email, password);
      if (result.status === "mfa_required") {
        navigate("/mfa/challenge", {
          state: { email, from: (location.state as LocationState | null)?.from },
          replace: true,
        });
        return;
      }
      await refresh();
      const target = (location.state as LocationState | null)?.from ?? "/";
      navigate(target, { replace: true });
    } catch (exc) {
      const apiError = exc as AuthApiError;
      // Surface a generic error so the API contract (don't reveal
      // whether the email exists) is preserved at the UI layer.
      setError(
        apiError.status === 429
          ? "Too many attempts. Try again in a minute."
          : "Invalid email or password.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={onSubmit} noValidate>
        <h1 className="auth-title">Sign in</h1>
        <label className="auth-field">
          <span className="auth-label">Email</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={error !== null}
          />
        </label>
        <label className="auth-field">
          <span className="auth-label">Password</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={error !== null}
          />
        </label>
        {error !== null && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}
        <button type="submit" className="auth-primary" disabled={busy}>
          {busy ? "Signing in..." : "Sign in"}
        </button>
        <div className="auth-links">
          <Link to="/reset/request">Forgot password?</Link>
          <Link to="/register">Create an account</Link>
        </div>
      </form>
    </div>
  );
}
